import * as React from "react";
import { Combobox } from "@base-ui/react/combobox";
import { useVirtualizer } from "@tanstack/react-virtual";
import VirtualizedComboboxList from "./Common/VirtualizedComboboxList";
import { useSeedsPortalContainer } from "./SeedsPortal";
import { useSuppressEscapeClear } from "./Common/useSuppressEscapeClear";
import ComboboxItem from "./Common/ComboboxItem";
import type { DataWithId } from "./Common/types";
import { groupItems } from "./Common/groupItems";
import {
  isCreatableSentinel,
  makeCreatableSentinel,
  type CreatableSentinel,
} from "./Common/sentinels";
import { Field } from "./Common/SelectStyles";
import {
  ComboboxTokenInputGroup,
  ComboboxTokenInput,
  ComboboxToken,
  ComboboxActionButtons,
  ComboboxClear,
  ComboboxTrigger,
  ComboboxPositioner,
  ComboboxPopup,
  ComboboxList,
  ComboboxEmpty,
  ComboboxStatus,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxSeparator,
  Token,
  TokenRemove,
  ComboboxGroupHeaderItem,
  ComboboxGroupHeaderCheckbox,
  ComboboxSelectAllItem,
} from "./Common/ComboboxStyles";
import { ClearIcon, StyledChevron, ChevronIcon } from "./Common/SelectIcons";
import Icon from "@sproutsocial/seeds-react-icon";

// ─── Sentinels ────────────────────────────────────────────────────────────────

interface GroupHeaderSentinel {
  __groupHeader: true;
  groupName: string;
}

function makeGroupHeaderSentinel(groupName: string): GroupHeaderSentinel {
  return { __groupHeader: true, groupName };
}

function isGroupHeaderSentinel(v: unknown): v is GroupHeaderSentinel {
  return typeof v === "object" && v !== null && "__groupHeader" in v;
}

interface SelectAllSentinel {
  __selectAll: true;
}

const SELECT_ALL_SENTINEL: SelectAllSentinel = { __selectAll: true };

function isSelectAllSentinel(v: unknown): v is SelectAllSentinel {
  return typeof v === "object" && v !== null && "__selectAll" in v;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface MultiComboboxBaseProps {
  id?: string;
  placeholder?: string;
  emptyText?: string;
  isLoading?: boolean;
  loadingText?: string;
  disabled?: boolean;
  filter?: ((item: unknown, query: string) => boolean) | null;
  inputValue?: string;
  onInputValueChange?: (value: string) => void;
  "aria-label"?: string;
  /** Id of a visible label. Preferred over `aria-label`. */
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  isInvalid?: boolean;
  required?: boolean;
  fullWidth?: boolean;
}

interface MultiComboboxDataProps<T extends DataWithId>
  extends MultiComboboxBaseProps {
  data: T[];
  itemToString: (item: T) => string;
  renderItem?: (item: T) => React.ReactNode;
  renderToken?: (item: T) => React.ReactNode;
  renderSelection?: (items: T[]) => React.ReactNode;
  inputType?: "icon" | "checkbox" | "none";
  selectedItemIds?: Array<T["id"]>;
  defaultSelectedItemIds?: Array<T["id"]>;
  onSelectedItemIdsChange?: (ids: Array<T["id"]>) => void;
  groupBy?: (item: T) => string;
  renderGroupHeading?: (label: string) => React.ReactNode;
  isGroupHeaderDisabled?: (groupName: string) => boolean;
  selectHeadings?: boolean;
  selectAll?: boolean | string;
  isVirtualized?: boolean;
  removeSelectedItems?: boolean;
  maxTokens?: number;
  creatable?: boolean;
  onCreate?: (value: string) => T;
  children?: never;
}

interface MultiComboboxChildrenProps extends MultiComboboxBaseProps {
  children: React.ReactNode;
  selectedItemIds?: Array<string | number>;
  defaultSelectedItemIds?: Array<string | number>;
  onSelectedItemIdsChange?: (ids: Array<string | number>) => void;
  data?: never;
  itemToString?: never;
  renderItem?: never;
  renderToken?: never;
  renderSelection?: never;
  inputType?: never;
  groupBy?: never;
  renderGroupHeading?: never;
  isGroupHeaderDisabled?: never;
  selectHeadings?: never;
  selectAll?: never;
  removeSelectedItems?: never;
  creatable?: never;
  onCreate?: never;
}

export type MultiComboboxProps<T extends DataWithId> =
  | MultiComboboxDataProps<T>
  | MultiComboboxChildrenProps;

// ─── Component ────────────────────────────────────────────────────────────────

// ─── Virtualized list ─────────────────────────────────────────────────────────

// ─── Component ────────────────────────────────────────────────────────────────

export function MultiCombobox<T extends DataWithId>(
  props: MultiComboboxProps<T>
) {
  const {
    id,
    placeholder = "Search...",
    emptyText = "No results found.",
    isLoading = false,
    loadingText = "Loading...",
    filter,
    inputValue,
    onInputValueChange,
    fullWidth = true,
  } = props;
  const ariaDescribedBy = props["aria-describedby"];
  const ariaLabel = props["aria-label"];
  const ariaLabelledBy = props["aria-labelledby"];
  const isInvalid = props.isInvalid;

  const isChildrenMode = "children" in props && props.children != null;

  const data = isChildrenMode
    ? ([] as T[])
    : (props as MultiComboboxDataProps<T>).data;
  const itemToString = isChildrenMode
    ? (_item: T) => ""
    : (props as MultiComboboxDataProps<T>).itemToString;
  const renderItem = isChildrenMode
    ? undefined
    : (props as MultiComboboxDataProps<T>).renderItem;
  const renderToken = isChildrenMode
    ? undefined
    : (props as MultiComboboxDataProps<T>).renderToken;
  const renderSelection = isChildrenMode
    ? undefined
    : (props as MultiComboboxDataProps<T>).renderSelection;
  const maxTokens = isChildrenMode
    ? undefined
    : (props as MultiComboboxDataProps<T>).maxTokens;
  const removeSelectedItems = isChildrenMode
    ? false
    : (props as MultiComboboxDataProps<T>).removeSelectedItems ?? false;
  const inputType = isChildrenMode
    ? "checkbox"
    : removeSelectedItems
    ? "none"
    : (props as MultiComboboxDataProps<T>).inputType ?? "checkbox";
  const groupBy = isChildrenMode
    ? undefined
    : (props as MultiComboboxDataProps<T>).groupBy;
  const renderGroupHeading = isChildrenMode
    ? undefined
    : (props as MultiComboboxDataProps<T>).renderGroupHeading;
  const isGroupHeaderDisabled = isChildrenMode
    ? undefined
    : (props as MultiComboboxDataProps<T>).isGroupHeaderDisabled;
  const selectHeadings = isChildrenMode
    ? false
    : (props as MultiComboboxDataProps<T>).selectHeadings ?? false;
  const selectAllProp = isChildrenMode
    ? false
    : (props as MultiComboboxDataProps<T>).selectAll ?? false;
  const selectAll = Boolean(selectAllProp);
  const selectAllLabel =
    typeof selectAllProp === "string" ? selectAllProp : "Select all";
  const isVirtualized = isChildrenMode
    ? false
    : (props as MultiComboboxDataProps<T>).isVirtualized ?? false;
  const creatableProp = isChildrenMode
    ? false
    : (props as MultiComboboxDataProps<T>).creatable ?? false;
  const onCreateProp = isChildrenMode
    ? undefined
    : (props as MultiComboboxDataProps<T>).onCreate;
  const children = isChildrenMode
    ? (props as MultiComboboxChildrenProps).children
    : undefined;

  const selectedItemIds = props.selectedItemIds;
  const defaultSelectedItemIds = props.defaultSelectedItemIds;
  const onSelectedItemIdsChange = props.onSelectedItemIdsChange;

  const { containerRef, portalContainer } = useSeedsPortalContainer();
  const [open, setOpen] = React.useState(false);
  const [internalInputValue, setInternalInputValue] = React.useState("");
  const skipNextInputChangeRef = React.useRef(false);
  const virtualizerRef = React.useRef<ReturnType<
    typeof useVirtualizer<HTMLDivElement, Element>
  > | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const onInputKeyDownCapture = useSuppressEscapeClear(open);

  const isControlled = selectedItemIds !== undefined;

  const idsToItems = React.useCallback(
    (ids: Array<T["id"]>): T[] =>
      ids
        .map((id) => data.find((d) => d.id === id))
        .filter((d): d is T => d != null),
    [data]
  );

  const [internalValue, setInternalValue] = React.useState<T[]>(() =>
    defaultSelectedItemIds
      ? idsToItems(defaultSelectedItemIds as Array<T["id"]>)
      : []
  );

  const value = isControlled
    ? idsToItems(selectedItemIds as Array<T["id"]>)
    : internalValue;

  const visibleData = React.useMemo(
    () =>
      removeSelectedItems
        ? data.filter((item) => !value.some((v) => v.id === item.id))
        : data,
    [data, value, removeSelectedItems]
  );

  const groups = React.useMemo(
    () => (groupBy ? groupItems(visibleData, groupBy) : null),
    [visibleData, groupBy]
  );

  const currentInputValue = inputValue ?? internalInputValue;

  const creatableSentinel = React.useMemo<CreatableSentinel | null>(() => {
    if (!creatableProp) return null;
    const trimmed = currentInputValue.trim();
    if (!trimmed) return null;
    const lowered = trimmed.toLocaleLowerCase();
    const exactExists = data.some(
      (d) => itemToString(d).trim().toLocaleLowerCase() === lowered
    );
    if (exactExists) return null;
    return makeCreatableSentinel(trimmed);
  }, [creatableProp, currentInputValue, data, itemToString]);

  // ─── Sentinel flat list ───────────────────────────────────────────────────
  // Used when selectHeadings or selectAll. selectAll implies selectHeadings —
  // group headers are always selectable when the list is flattened.

  type FlatRow =
    | T
    | GroupHeaderSentinel
    | SelectAllSentinel
    | CreatableSentinel;

  const flatItems = React.useMemo<FlatRow[] | null>(() => {
    if (!selectHeadings && !selectAll) return null;
    if (!groups && !selectAll) return null;
    const rows: FlatRow[] = [];
    if (selectAll) rows.push(SELECT_ALL_SENTINEL);
    if (groups) {
      for (const group of groups) {
        rows.push(makeGroupHeaderSentinel(group.value));
        rows.push(...group.items);
      }
    } else {
      rows.push(...visibleData);
    }
    return rows;
  }, [groups, visibleData, selectHeadings, selectAll]);

  function setSelectedItems(items: T[]) {
    if (!isControlled) setInternalValue(items);
    if (onSelectedItemIdsChange)
      onSelectedItemIdsChange(items.map((item) => item.id));
  }

  function handleValueChange(next: FlatRow[]) {
    const creatableClicked = next.find(isCreatableSentinel);
    if (creatableClicked) {
      const newItem = onCreateProp?.(creatableClicked.label);
      const realItems = next.filter(
        (v): v is T =>
          !isGroupHeaderSentinel(v) &&
          !isSelectAllSentinel(v) &&
          !isCreatableSentinel(v)
      );
      setSelectedItems(newItem ? [...realItems, newItem] : realItems);
      skipNextInputChangeRef.current = true;
      setInternalInputValue("");
      onInputValueChange?.("");
      return;
    }

    const selectAllClicked = next.filter(isSelectAllSentinel);
    const groupSentinels = next.filter(isGroupHeaderSentinel);
    const realItems = next.filter(
      (v): v is T => !isGroupHeaderSentinel(v) && !isSelectAllSentinel(v)
    );

    if (selectAllClicked.length > 0) {
      const allSelected = data.every((d) =>
        realItems.some((s) => s.id === d.id)
      );
      setSelectedItems(allSelected ? [] : [...data]);
      return;
    }

    if (groupSentinels.length === 0) {
      setSelectedItems(realItems);
      return;
    }

    let updated = [...realItems];
    for (const sentinel of groupSentinels) {
      if (isGroupHeaderDisabled?.(sentinel.groupName)) continue;
      const group = groups?.find((g) => g.value === sentinel.groupName);
      if (!group) continue;
      const allSelected = group.items.every((item) =>
        updated.some((s) => s.id === item.id)
      );
      if (allSelected) {
        updated = updated.filter(
          (s) => !group.items.some((item) => item.id === s.id)
        );
      } else {
        const missing = group.items.filter(
          (item) => !updated.some((s) => s.id === item.id)
        );
        updated = [...updated, ...missing];
      }
    }
    setSelectedItems(updated);
  }

  function handleSimpleValueChange(newItems: FlatRow[]) {
    const creatableClicked = newItems.find(isCreatableSentinel);
    if (creatableClicked) {
      const newItem = onCreateProp?.(creatableClicked.label);
      const realItems = newItems.filter((v): v is T => !isCreatableSentinel(v));
      setSelectedItems(newItem ? [...realItems, newItem] : realItems);
      skipNextInputChangeRef.current = true;
      setInternalInputValue("");
      onInputValueChange?.("");
      return;
    }
    setSelectedItems(newItems as T[]);
  }

  function removeItem(item: T, e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setSelectedItems(value.filter((v) => v.id !== item.id));
    inputRef.current?.focus();
  }

  // ─── Render sentinel item ─────────────────────────────────────────────────

  function renderSentinelRow(row: FlatRow) {
    if (isSelectAllSentinel(row)) {
      const totalCount = data.length;
      const selectedCount = value.length;
      const state: "none" | "partial" | "all" =
        selectedCount === 0
          ? "none"
          : selectedCount === totalCount
          ? "all"
          : "partial";
      return (
        <ComboboxSelectAllItem key="__selectAll" value={row}>
          <ComboboxGroupHeaderCheckbox $state={state} id="__selectAll" />
          {selectAllLabel}
        </ComboboxSelectAllItem>
      );
    }

    if (isGroupHeaderSentinel(row)) {
      const group = groups?.find((g) => g.value === row.groupName);
      const groupItemsList = group?.items ?? [];
      const selectedCount = groupItemsList.filter((item) =>
        value.some((s) => s.id === item.id)
      ).length;
      const state: "none" | "partial" | "all" =
        selectedCount === 0
          ? "none"
          : selectedCount === groupItemsList.length
          ? "all"
          : "partial";
      return (
        <ComboboxGroupHeaderItem
          key={`__header-${row.groupName}`}
          value={row}
          disabled={isGroupHeaderDisabled?.(row.groupName)}
        >
          <ComboboxGroupHeaderCheckbox
            $state={state}
            id={`__header-${row.groupName}`}
          />
          {renderGroupHeading
            ? renderGroupHeading(row.groupName)
            : row.groupName}
        </ComboboxGroupHeaderItem>
      );
    }

    if (isCreatableSentinel(row)) {
      return renderCreatableItem(row);
    }

    const item = row as T;
    return (
      <ComboboxItem
        key={item.id}
        value={item}
        itemId={String(item.id)}
        label={itemToString(item)}
        disabled={item.disabled}
        inputType={inputType}
        renderItem={
          renderItem ? () => renderItem(item) : () => itemToString(item)
        }
      />
    );
  }

  function renderCreatableItem(
    sentinel: CreatableSentinel,
    style?: React.CSSProperties,
    index?: number,
    measureRef?: (el: Element | null) => void
  ) {
    return (
      <ComboboxItem
        key="__creatable"
        value={sentinel}
        itemId="__creatable"
        label={`Create "${sentinel.label}"`}
        inputType="none"
        renderItem={() => (
          <>
            <Icon name="plus-outline" size="mini" />
            {`Create "${sentinel.label}"`}
          </>
        )}
        style={style}
        index={index}
        data-index={index}
        ref={measureRef}
      />
    );
  }

  // ─── Items passed to Combobox.Root ────────────────────────────────────────
  // When selectHeadings: flatItems (flat array of group header sentinels + items).
  // When selectAll only: prepend SELECT_ALL_SENTINEL to data so Base UI tracks
  //   it, while Combobox.List still uses the normal grouped render.
  // The sentinel is never in flatItems — it is always rendered manually above
  // Combobox.List, so we must not prepend it when flatItems is used.

  const baseItems =
    flatItems ?? groups ?? (isChildrenMode ? undefined : visibleData);
  const rootItems =
    creatableSentinel && Array.isArray(baseItems)
      ? ([...baseItems, creatableSentinel] as FlatRow[])
      : baseItems;

  const hasSentinels = selectAll || selectHeadings;

  const rootValue = hasSentinels
    ? ([...value] as FlatRow[])
    : (value as unknown as T[]);

  return (
    <Field $fullWidth={fullWidth}>
      <div ref={containerRef} style={{ position: "relative" }} />
      <Combobox.Root
        multiple
        id={id}
        virtualized={isVirtualized}
        open={open}
        disabled={props.disabled}
        onOpenChange={(nextOpen, eventDetails) => {
          if (!nextOpen && eventDetails.reason === "item-press") {
            eventDetails.cancel();
            return;
          }
          setOpen(nextOpen);
        }}
        filter={
          creatableProp
            ? (item: unknown, query: string) => {
                if (isCreatableSentinel(item)) return true;
                if (filter) return filter(item, query);
                // Default Base UI filter: match against itemToStringLabel
                if (isGroupHeaderSentinel(item) || isSelectAllSentinel(item))
                  return true;
                const label = itemToString(item as T);
                return label.toLowerCase().includes(query.toLowerCase());
              }
            : filter
        }
        inputValue={
          creatableProp ? currentInputValue : inputValue ?? internalInputValue
        }
        onInputValueChange={(v, eventDetails) => {
          // Preserve search query when an item is selected. Base UI fires
          // 'input-clear' on item-press; ignore it while the popup stays open.
          if (eventDetails.reason === "input-clear" && open) return;

          if (creatableProp) {
            if (skipNextInputChangeRef.current) {
              skipNextInputChangeRef.current = false;
              return;
            }
            setInternalInputValue(v);
            onInputValueChange?.(v);
          } else {
            if (inputValue === undefined) {
              setInternalInputValue(v);
            }
            onInputValueChange?.(v);
          }
        }}
        onItemHighlighted={
          isVirtualized
            ? (item, { reason, index }) => {
                const virt = virtualizerRef.current;
                if (!item || !virt) return;
                const isStart = index === 0;
                const isEnd = index === virt.options.count - 1;
                // Only handle keyboard wrap-around: when navigation loops from
                // the last item to the first (or vice versa), the wrapped item
                // is outside the virtualized window and standard scroll-into-
                // view can't find it.
                if (reason === "keyboard" && (isStart || isEnd)) {
                  queueMicrotask(() => {
                    virt.scrollToIndex(index, {
                      align: isStart ? "start" : "end",
                    });
                  });
                }
              }
            : undefined
        }
        value={rootValue}
        onValueChange={
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (hasSentinels ? handleValueChange : handleSimpleValueChange) as any
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        itemToStringLabel={(item: any) => {
          if (
            !item ||
            isGroupHeaderSentinel(item) ||
            isSelectAllSentinel(item) ||
            isCreatableSentinel(item)
          )
            return "";
          return itemToString(item as T);
        }}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        isItemEqualToValue={(a: any, b: any) => {
          if (isCreatableSentinel(a) && isCreatableSentinel(b))
            return a.label === b.label;
          if (isGroupHeaderSentinel(a) && isGroupHeaderSentinel(b))
            return a.groupName === b.groupName;
          if (isSelectAllSentinel(a) && isSelectAllSentinel(b)) return true;
          if (
            !isGroupHeaderSentinel(a) &&
            !isSelectAllSentinel(a) &&
            !isCreatableSentinel(a) &&
            !isGroupHeaderSentinel(b) &&
            !isSelectAllSentinel(b) &&
            !isCreatableSentinel(b)
          )
            return (a as T).id === (b as T).id;
          return false;
        }}
        items={rootItems}
      >
        <ComboboxTokenInputGroup
          $isInvalid={isInvalid}
          onKeyDownCapture={onInputKeyDownCapture}
        >
          <ComboboxToken>
            {renderSelection
              ? renderSelection(value)
              : (() => {
                  const visibleTokens =
                    maxTokens != null ? value.slice(0, maxTokens) : value;
                  const overflow =
                    maxTokens != null ? value.length - maxTokens : 0;
                  return (
                    <>
                      {visibleTokens.map((item) => (
                        <Token key={item.id}>
                          {renderToken ? renderToken(item) : itemToString(item)}
                          <TokenRemove
                            aria-label={`Remove ${itemToString(item)}`}
                            onPointerDown={(e) => e.preventDefault()}
                            onClick={(e) => removeItem(item, e)}
                          >
                            <Icon name="x-outline" size="mini" />
                          </TokenRemove>
                        </Token>
                      ))}
                      {overflow > 0 && (
                        <Token key="__overflow">+{overflow}</Token>
                      )}
                    </>
                  );
                })()}
            <ComboboxTokenInput
              ref={inputRef}
              id={id}
              placeholder={value.length > 0 ? "" : placeholder}
              aria-describedby={ariaDescribedBy}
              aria-invalid={isInvalid || undefined}
              aria-required={props.required || undefined}
            />
            <ComboboxActionButtons>
              <ComboboxClear aria-label="Clear all">
                <ClearIcon />
              </ComboboxClear>
              <ComboboxTrigger aria-label="Open popup">
                <StyledChevron $isOpen={open}>
                  <ChevronIcon />
                </StyledChevron>
              </ComboboxTrigger>
            </ComboboxActionButtons>
          </ComboboxToken>
        </ComboboxTokenInputGroup>

        <Combobox.Portal container={portalContainer}>
          <ComboboxPositioner sideOffset={4}>
            <ComboboxPopup
              aria-busy={isLoading || undefined}
              style={
                isVirtualized
                  ? { display: "flex", flexDirection: "column" }
                  : undefined
              }
            >
              <ComboboxStatus>{isLoading ? loadingText : null}</ComboboxStatus>
              <ComboboxEmpty>{isLoading ? null : emptyText}</ComboboxEmpty>
              <ComboboxList
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                style={
                  isVirtualized
                    ? {
                        padding: 0,
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                      }
                    : undefined
                }
              >
                {isVirtualized ? (
                  <VirtualizedComboboxList
                    open={open}
                    virtualizerRef={virtualizerRef}
                    value={value}
                    groups={groups}
                    data={visibleData}
                    itemToString={itemToString}
                    inputType={inputType}
                    renderItem={renderItem}
                    renderGroupHeading={renderGroupHeading}
                    isGroupHeaderDisabled={isGroupHeaderDisabled}
                    renderCreatableItem={
                      creatableProp
                        ? (sentinel, style, index, measureRef) =>
                            renderCreatableItem(
                              sentinel,
                              style,
                              index,
                              measureRef
                            )
                        : undefined
                    }
                  />
                ) : children ? (
                  children
                ) : flatItems ? (
                  (row: FlatRow) => renderSentinelRow(row)
                ) : groups ? (
                  (
                    group: { value: string; items: T[] } | CreatableSentinel,
                    index: number
                  ) => {
                    if (isCreatableSentinel(group)) {
                      return renderCreatableItem(group);
                    }
                    const g = group as { value: string; items: T[] };
                    return (
                      <React.Fragment key={g.value}>
                        <ComboboxGroup items={g.items}>
                          <ComboboxGroupLabel>
                            {renderGroupHeading
                              ? renderGroupHeading(g.value)
                              : g.value}
                          </ComboboxGroupLabel>
                          <Combobox.Collection>
                            {(item: T) => (
                              <ComboboxItem
                                key={item.id}
                                value={item}
                                itemId={String(item.id)}
                                label={itemToString(item)}
                                disabled={item.disabled}
                                inputType={inputType}
                                renderItem={
                                  renderItem
                                    ? () => renderItem(item)
                                    : () => itemToString(item)
                                }
                              />
                            )}
                          </Combobox.Collection>
                        </ComboboxGroup>
                        {index < groups.length - 1 && <ComboboxSeparator />}
                      </React.Fragment>
                    );
                  }
                ) : (
                  (item: T | CreatableSentinel) => {
                    if (isCreatableSentinel(item)) {
                      return renderCreatableItem(item);
                    }
                    const dataItem = item as T;
                    return (
                      <ComboboxItem
                        key={dataItem.id}
                        value={dataItem}
                        itemId={String(dataItem.id)}
                        label={itemToString(dataItem)}
                        disabled={dataItem.disabled}
                        inputType={inputType}
                        renderItem={
                          renderItem
                            ? () => renderItem(dataItem)
                            : () => itemToString(dataItem)
                        }
                      />
                    );
                  }
                )}
              </ComboboxList>
            </ComboboxPopup>
          </ComboboxPositioner>
        </Combobox.Portal>
      </Combobox.Root>
    </Field>
  );
}
